"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import { X } from "lucide-react"
import CookiePreferencesModal, { CookiePreferences } from "./cookie-preferences-modal"

const GA_DISABLE_KEY = 'ga-disable-GTM-KZD4GXL4' as const

export default function CookieBanner() {
  const [isVisible, setIsVisible] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false,
  })

  useEffect(() => {
    // Check if user has already made a choice
    const storedPreferences = localStorage.getItem('cookiePreferences')
    if (!storedPreferences) {
      setIsVisible(true)
    } else {
      const parsedPreferences = JSON.parse(storedPreferences) as CookiePreferences
      setPreferences(parsedPreferences)
      updateAnalytics(parsedPreferences)
    }
  }, [])

  const updateAnalytics = (newPreferences: CookiePreferences) => {
    if (typeof window !== 'undefined') {
      if (newPreferences.analytics) {
        // Enable analytics
        window[GA_DISABLE_KEY] = false
      } else {
        // Disable analytics
        window[GA_DISABLE_KEY] = true
      }

      // Update dataLayer with user preferences
      window.dataLayer?.push({
        event: 'cookiePreferencesUpdated',
        cookiePreferences: newPreferences,
      })
    }
  }

  const acceptAll = () => {
    const newPreferences: CookiePreferences = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
    }
    savePreferences(newPreferences)
  }

  const acceptNecessary = () => {
    const newPreferences: CookiePreferences = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
    }
    savePreferences(newPreferences)
  }

  const savePreferences = (newPreferences: CookiePreferences) => {
    localStorage.setItem('cookiePreferences', JSON.stringify(newPreferences))
    setPreferences(newPreferences)
    updateAnalytics(newPreferences)
    setIsVisible(false)
    setShowPreferences(false)
  }

  if (!isVisible && !showPreferences) return null

  return (
    <>
      {isVisible && (
        <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t shadow-lg p-4 md:p-6 z-50">
          <div className="container mx-auto">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="pr-8">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  We use cookies to enhance your experience. By continuing to visit this site you agree to our use of cookies.{' '}
                  <button
                    onClick={() => setShowPreferences(true)}
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Cookie Policy
                  </button>
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                <button
                  onClick={acceptNecessary}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                >
                  Necessary Only
                </button>
                <button
                  onClick={acceptAll}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Accept All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <CookiePreferencesModal
        isOpen={showPreferences}
        onClose={() => setShowPreferences(false)}
        onSave={savePreferences}
        initialPreferences={preferences}
      />
    </>
  )
} 